import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, 
  MapPin, 
  Briefcase, 
  Plus, 
  X, 
  Loader2, 
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US States
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

export default function PortalBuild() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [session, setSession] = useState(null);
  const [industryPacks, setIndustryPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: "",
    industry_key: "",
    city: "",
    state: "",
    services: [],
    service_areas: [],
  });
  
  const [newService, setNewService] = useState("");
  const [newArea, setNewArea] = useState("");

  useEffect(() => {
    checkSession();
    fetchIndustryPacks();
  }, []);

  const checkSession = async () => {
    try {
      const response = await axios.get(`${API}/portal/auth/session`, {
        withCredentials: true
      });
      
      if (response.data.authenticated) {
        setSession(response.data);
        
        // If customer has existing build, redirect appropriately
        if (response.data.current_build) {
          const build = response.data.current_build;
          if (build.portal_status === "final_ready" || build.portal_status === "paid" || build.portal_status === "building_final") {
            navigate(`/portal/download/${build.lead_id}`);
            return;
          } else if (build.portal_status === "preview_ready" || build.portal_status === "awaiting_payment") {
            navigate(`/portal/preview/${build.lead_id}`);
            return;
          }
        }
      } else {
        // No session - redirect to landing
        navigate("/");
        return;
      }
    } catch (error) {
      console.error("Session check failed:", error);
      navigate("/");
      return;
    } finally {
      setCheckingSession(false);
    }
  };

  const fetchIndustryPacks = async () => {
    try {
      // Use portal-specific endpoint that doesn't require admin auth
      const response = await axios.get(`${API}/portal/industry-packs`, {
        withCredentials: true
      });
      setIndustryPacks(response.data);
      console.log(`Loaded ${response.data.length} industry packs`);
    } catch (error) {
      console.error("Failed to fetch industry packs:", error);
      // Show user-friendly error
      const errorMsg = error.response?.data?.detail || "Failed to load industries";
      toast.error(errorMsg);
      // Fallback to categories
      try {
        const catResponse = await axios.get(`${API}/categories`, {
          withCredentials: true
        });
        const categories = catResponse.data.categories || [];
        setIndustryPacks(categories.map(c => ({ 
          industry_key: c.toLowerCase().replace(/\s+/g, '_'),
          display_name: c,
          default_services: [],
          default_service_areas: []
        })));
      } catch (e) {
        console.error("Failed to fetch categories:", e);
        toast.error("Unable to load industry options. Please refresh the page.");
      }
    }
  };

  const handlePackChange = (packKey) => {
    const pack = industryPacks.find(p => p.industry_key === packKey);
    setSelectedPack(pack);
    setFormData(prev => ({
      ...prev,
      industry_key: packKey,
      services: pack?.default_services || [],
      service_areas: pack?.default_service_areas || [],
    }));
  };

  const addService = () => {
    if (newService.trim() && !formData.services.includes(newService.trim())) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService("");
    }
  };

  const removeService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== service)
    }));
  };

  const addServiceArea = () => {
    if (newArea.trim() && !formData.service_areas.includes(newArea.trim())) {
      setFormData(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, newArea.trim()]
      }));
      setNewArea("");
    }
  };

  const removeServiceArea = (area) => {
    setFormData(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter(a => a !== area)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!formData.industry_key) {
      toast.error("Please select an industry");
      return;
    }
    if (!formData.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!formData.state) {
      toast.error("State is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/build`, formData, {
        withCredentials: true
      });
      
      toast.success("Generating your preview...");
      navigate(`/portal/preview/${response.data.lead_id}`);
      
    } catch (error) {
      console.error("Failed to create build:", error);
      toast.error(error.response?.data?.detail || "Failed to create build");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Signed in as {session?.email}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Build Your Website</h1>
          <p className="text-muted-foreground mt-1">
            Tell us about your business and we will create a preview for you.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 size={20} />
                Business Information
              </CardTitle>
              <CardDescription>
                Basic details about your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    placeholder="e.g., Smith's Plumbing"
                    value={formData.business_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                    data-testid="portal-business-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select 
                    value={formData.industry_key} 
                    onValueChange={handlePackChange}
                  >
                    <SelectTrigger data-testid="portal-industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industryPacks.map((pack) => (
                        <SelectItem key={pack.industry_key} value={pack.industry_key}>
                          {pack.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="e.g., Austin"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    data-testid="portal-city"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(val) => setFormData(prev => ({ ...prev, state: val }))}
                  >
                    <SelectTrigger data-testid="portal-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase size={20} />
                Services
              </CardTitle>
              <CardDescription>
                What services does your business offer?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {formData.services.map((service, i) => (
                  <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1">
                    {service}
                    <button
                      type="button"
                      onClick={() => removeService(service)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
                {formData.services.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Select an industry to load default services, or add your own.
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a service..."
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                />
                <Button type="button" variant="outline" onClick={addService}>
                  <Plus size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Service Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin size={20} />
                Service Areas
              </CardTitle>
              <CardDescription>
                Cities or areas you serve (optional - we will generate from your city if empty)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {formData.service_areas.map((area, i) => (
                  <Badge key={i} variant="outline" className="flex items-center gap-1 py-1">
                    {area}
                    <button
                      type="button"
                      onClick={() => removeServiceArea(area)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
                {formData.service_areas.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Leave empty to auto-generate from your city.
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a service area..."
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addServiceArea())}
                />
                <Button type="button" variant="outline" onClick={addServiceArea}>
                  <Plus size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              size="lg"
              disabled={loading}
              data-testid="portal-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Generating Preview...
                </>
              ) : (
                <>
                  Generate Preview
                  <ArrowRight className="ml-2" size={18} />
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
